import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { 
  TrendingUp, Euro, Calendar, BarChart3, 
  Loader2, RefreshCw, Award, ShoppingCart
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const VendorAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [customDates, setCustomDates] = useState({
    start: '',
    end: ''
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      let url = `/vendor/analytics?period=${period}`;
      
      if (period === 'custom' && customDates.start && customDates.end) {
        url += `&start_date=${customDates.start}&end_date=${customDates.end}`;
      }
      
      const response = await api.get(url);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Erreur chargement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateSubmit = () => {
    if (customDates.start && customDates.end) {
      fetchAnalytics();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center text-[#B8B8BA] py-12">
        Aucune donnée disponible
      </div>
    );
  }

  const { summary, charts, tier } = analytics;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">📊 Mes Statistiques</h1>
            <p className="text-[#B8B8BA]">Suivez vos performances commerciales</p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none"
            >
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
              <option value="365d">Dernière année</option>
              <option value="custom">Période personnalisée</option>
            </select>
            
            <button
              onClick={fetchAnalytics}
              className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg hover:border-[#00FFFF] transition-colors text-white"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
          </div>
        </div>

        {/* Dates personnalisées */}
        {period === 'custom' && (
          <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-4 flex items-center gap-4 mb-6">
            <input
              type="date"
              value={customDates.start}
              onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none"
            />
            <span className="text-[#B8B8BA]">à</span>
            <input
              type="date"
              value={customDates.end}
              onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none"
            />
            <button
              onClick={handleCustomDateSubmit}
              className="px-6 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:shadow-lg transition-all"
            >
              Appliquer
            </button>
          </div>
        )}

        {/* Cartes KPI */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <ShoppingCart className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Ventes (Période)</p>
                <p className="text-3xl font-bold text-white">{summary.total_sales_period}</p>
              </div>
            </div>
            <p className="text-[#B8B8BA] text-sm">Total : {summary.total_sales_all_time} ventes</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Euro className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Revenus (Période)</p>
                <p className="text-3xl font-bold text-white">{summary.revenue_period.toFixed(0)}€</p>
              </div>
            </div>
            <p className="text-[#B8B8BA] text-sm">Clients actifs : {summary.active_clients}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Commission (Période)</p>
                <p className="text-3xl font-bold text-white">{summary.commission_period.toFixed(0)}€</p>
              </div>
            </div>
            <p className="text-[#B8B8BA] text-sm">Total gagné : {summary.total_earned.toFixed(0)}€</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <Award className="h-6 w-6 text-orange-400" />
              </div>
              <div>
                <p className="text-[#B8B8BA] text-sm">Palier Actuel</p>
                <p className="text-2xl font-bold text-white">{tier.name}</p>
              </div>
            </div>
            <p className="text-[#FFB800] text-sm font-semibold">{tier.commission_rate}€ par vente</p>
          </motion.div>
        </div>

        {/* Graphique Évolution des Ventes */}
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#00FFFF]" />
            Évolution de Vos Ventes
          </h3>
          {charts.sales_evolution && charts.sales_evolution.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={charts.sales_evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3A3A3D" />
                <XAxis dataKey="date" stroke="#B8B8BA" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" stroke="#B8B8BA" />
                <YAxis yAxisId="right" orientation="right" stroke="#B8B8BA" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#16161A', 
                    border: '1px solid #3A3A3D',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="sales_count" 
                  stroke="#00FFFF" 
                  strokeWidth={3}
                  dot={{ fill: '#00FFFF', r: 4 }}
                  name="Nombre de ventes"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="commission" 
                  stroke="#00FF88" 
                  strokeWidth={3}
                  dot={{ fill: '#00FF88', r: 4 }}
                  name="Commission (€)"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-[#B8B8BA] py-12">Aucune vente pour cette période</div>
          )}
        </div>

        {/* Balance Summary */}
        <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Euro className="h-5 w-5 text-[#00FF88]" />
            Détail de Votre Balance
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-[#B8B8BA] text-sm mb-1">Disponible</p>
              <p className="text-2xl font-bold text-[#00FF88]">{summary.balance_available.toFixed(2)}€</p>
            </div>
            <div>
              <p className="text-[#B8B8BA] text-sm mb-1">En Attente</p>
              <p className="text-2xl font-bold text-[#FFB800]">{summary.balance_pending.toFixed(2)}€</p>
            </div>
            <div>
              <p className="text-[#B8B8BA] text-sm mb-1">En Retrait</p>
              <p className="text-2xl font-bold text-[#FF6B35]">{summary.balance_in_withdrawal.toFixed(2)}€</p>
            </div>
            <div>
              <p className="text-[#B8B8BA] text-sm mb-1">Déjà Retiré</p>
              <p className="text-2xl font-bold text-white">{summary.balance_withdrawn.toFixed(2)}€</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorAnalytics;
